import { useEffect, useRef, useState } from "react";
import { Microphone, Stop, ArrowClockwise, MagicWand, UploadSimple, SpinnerGap } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function VoiceRecorder({ onTranscribe, onGenerate, busy }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => () => {
    clearInterval(timerRef.current);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, [audioUrl]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: "audio/webm" });
        setBlob(b);
        setAudioUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(b); });
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setSeconds(0);
      setBlob(null);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      toast.error("Microphone access denied. Allow mic permission and try again.");
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    mediaRef.current?.stop();
    setRecording(false);
  };

  const reset = () => {
    setBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setSeconds(0);
  };

  const toFile = () => new File([blob], `voice-memo-${Date.now()}.webm`, { type: "audio/webm" });

  return (
    <div className="mt-1.5 rounded-md border border-dashed border-border p-6 text-center" data-testid="voice-recorder">
      {!blob ? (
        <div className="flex flex-col items-center gap-3">
          {recording ? (
            <>
              <div className="flex items-center gap-2 text-primary">
                <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                <span className="font-mono text-lg" data-testid="voice-timer">{fmt(seconds)}</span>
              </div>
              <Button variant="destructive" data-testid="voice-stop-btn" onClick={stop}>
                <Stop size={18} weight="fill" className="mr-1.5" /> Stop recording
              </Button>
            </>
          ) : (
            <>
              <Microphone size={30} className="text-primary" />
              <p className="text-sm font-medium">Record a voice memo</p>
              <p className="text-xs text-muted-foreground">Talk through your idea — we'll turn it into a week of posts.</p>
              <Button data-testid="voice-record-btn" onClick={start}>
                <Microphone size={18} weight="fill" className="mr-1.5" /> Start recording
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4">
          <audio controls src={audioUrl} className="w-full max-w-sm h-10" data-testid="voice-playback" />
          <span className="text-xs font-mono text-muted-foreground">Recorded {fmt(seconds)}</span>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button variant="ghost" size="sm" data-testid="voice-rerecord-btn" onClick={reset} disabled={busy}>
              <ArrowClockwise size={16} className="mr-1.5" /> Re-record
            </Button>
            <Button variant="outline" data-testid="voice-transcribe-btn" onClick={() => onTranscribe(toFile())} disabled={busy}>
              {busy ? <SpinnerGap size={16} className="animate-spin mr-1.5" /> : <UploadSimple size={16} className="mr-1.5" />} Transcribe only
            </Button>
            <Button data-testid="voice-generate-btn" onClick={() => onGenerate(toFile())} disabled={busy}>
              {busy ? <SpinnerGap size={16} className="animate-spin mr-1.5" /> : <MagicWand size={16} weight="fill" className="mr-1.5" />} Transcribe &amp; generate pack
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
